import { useCallback, useContext, useMemo } from 'react'
import styled from 'styled-components'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { calcGridPositionsForVerticalMonth, getLocalDate } from 'lib/datetime/calendarUtils'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { CALENDAR_GA_EVENT_ACTION } from 'constants/analytics'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import moment from 'moment'
import OfferBundleAndSaveCalendarSidebarDayCell from './OfferBundleAndSaveCalendarSidebarDayCell'
import UserAnalyticsActionContext from 'contexts/userAnalyticsActionContext'
import { shouldEnableCalendarRecommendations } from 'selectors/featuresSelectors'
import type { BundledOfferState } from 'contexts/OfferPage/offerPageStateReducer'
import {
  OfferPageActions,
  takeCurrentOfferState,
} from 'contexts/OfferPage/offerPageStateReducer'
import { getCalendarCheapestDays } from 'selectors/offerPage/offerPageSelectors'
import useBundleAndSaveCalendarDayPickingUtils from 'hooks/Offers/BundleAndSave/useBundleAndSaveCalendarDayPickingUtils'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { isVillaOffer } from 'lib/offer/offerTypes'
import { isBundleAndSaveV2Enabled } from 'lib/offer/offerUtils'
import { getComplementaryBundleState } from 'selectors/offerPage/bundledOfferPageSelectors'
import { useIntl } from 'react-intl'

const Grid = styled.div`
  display: grid;
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  border-bottom: 0;
  position: relative;
  grid-template-columns: 100%;

  ${mediaQueryUp.tablet} {
    grid-template-columns: repeat(7 , 1fr);
    border: none;
  }
`

const Root = styled.div`
  width: 100%;
`

interface Props {
  days: Array<App.CalendarDay>
  tacticalDays: Array<string>
  enableCalendarRecommendations: boolean
  selectedDay?: App.CalendarDay
  className?: string
  bundleOfferId?: string
  currentOfferId?: string
  duration: number
  complementaryBundleState?: BundledOfferState
  cacheActive?: boolean
  otherAvailability: { [checkIn: string]: boolean }
  isRoomSelected?: boolean
  flights?: boolean
  flightsOnly?: boolean
  cheapestDays: Record<string, App.CalendarCheapestDay>
  offer: App.Offer
  displayMemberPrice: boolean
}

function OfferBundleAndSaveCalendarSidebarDaysMonth(props: Props) {
  const {
    days,
    tacticalDays,
    className,
    duration,
    enableCalendarRecommendations,
    otherAvailability,
    selectedDay,
    isRoomSelected,
    flights,
    flightsOnly,
    bundleOfferId,
    currentOfferId,
    complementaryBundleState,
    cheapestDays,
    offer,
    displayMemberPrice,
  } = props

  const intl = useIntl()
  const onUserAnalyticsAction = useContext(UserAnalyticsActionContext)

  const firstDate = days[0]

  const selectedRange = useMemo(() => {
    if (selectedDay) {
      return calcGridPositionsForVerticalMonth(
        getLocalDate(firstDate.checkIn),
        getLocalDate(selectedDay.checkIn),
        getLocalDate(selectedDay.checkOut),
      ).range
    }
    return undefined
  }, [selectedDay, firstDate])

  const {
    isComplementaryBundleCell,
    isComplementaryCheckInCell,
    isComplementaryCheckOutCell,
    isIllegalBundleSelection,
  } = useBundleAndSaveCalendarDayPickingUtils(complementaryBundleState, duration)

  const onDayClick = useCallback((day: App.CalendarDay) => {
    onUserAnalyticsAction?.(CALENDAR_GA_EVENT_ACTION.CheckIn, moment(day.checkIn).format(ISO_DATE_FORMAT))
  }, [onUserAnalyticsAction])

  const dispatch = useOfferPageDispatch()

  const overlapWarningMessage = intl.formatMessage({
    id: 'UyhtwyZWGgHJpTit1Boz',
    defaultMessage: 'Check-in date overlaps with your other stay. Please select a new date.',
    description: 'Snackbar warning when bundle check-in date overlaps with another stay',
  })
  const swapOrderWarningMessage = intl.formatMessage({
    id: 'HJJkbda8ihtUmmdsSzXq',
    defaultMessage: 'The dates for Hotel 2 must come after Hotel 1. Try swapping the order of the hotels.',
    description: 'Snackbar warning when bundle hotel dates are in wrong order',
  })
  const swapOrderLabel = intl.formatMessage({
    id: 'tNFEXsYq9Y5DKqExoJFM',
    defaultMessage: 'Swap order',
    description: 'Action button label to swap the order of bundled hotels',
  })

  const onInvalidBundleDateClick = useCallback(
    () => {
      if (isBundleAndSaveV2Enabled()) {
        showSnackbar(overlapWarningMessage, 'warning')
      } else {
        showSnackbar(
          swapOrderWarningMessage, 'warning',
          {
            action: {
              label: swapOrderLabel,
              onAction: () => {
                dispatch({ type: OfferPageActions.openSwapModal })
              },
            },
          },
        )
      }
    },
    [dispatch, overlapWarningMessage, swapOrderWarningMessage, swapOrderLabel],
  )

  return (
    <Root className={className}>
      <Grid>
        {days.map((day, index) => {
          const isCheckIn = day.checkIn === selectedDay?.checkIn
          const isCheckOut = day.checkIn === selectedDay?.checkOut
          const isBetweenDay = !!(selectedRange && (index + 1) >= selectedRange.start && index < (selectedRange.end - 1))
          const isInSelectedRange = !!(selectedDay?.checkIn && selectedDay.checkOut) && (isCheckIn || isCheckOut || isBetweenDay)

          return <OfferBundleAndSaveCalendarSidebarDayCell
            key={day.checkIn}
            enableCalendarRecommendations={enableCalendarRecommendations}
            duration={duration}
            bundleOfferId={bundleOfferId}
            day={day}
            hasOtherAvailability={otherAvailability[day.checkIn] && !currentOfferId}
            isCheckIn={isCheckIn}
            isCheckOut={isCheckOut}
            isBetweenDay={isBetweenDay}
            isPriceVisible={!isInSelectedRange || isCheckIn}
            isRoomSelected={isRoomSelected}
            flightsOnly={flightsOnly}
            flights={flights}
            currentOfferId={currentOfferId}
            isComplementaryBundleCell={isComplementaryBundleCell(day)}
            isComplementaryCheckIn={isComplementaryCheckInCell(day)}
            isComplementaryCheckOut={isComplementaryCheckOutCell(day)}
            isIllegalBundleSelection={isIllegalBundleSelection(day)}
            complementaryPropertyName={complementaryBundleState?.offer?.property?.name}
            hasPromo={!!day.dealId}
            isTacticalPromotion={tacticalDays.includes(day.checkIn) && day.hotelAvailable}
            onClick={onDayClick}
            cheapestDay={cheapestDays[day.uniqueKey] ?? null}
            onInvalidBundleDateClick={onInvalidBundleDateClick}
            showUrgencyLabels={!isVillaOffer(offer)}
            displayMemberPrice={displayMemberPrice}
          />
        })}
      </Grid>
    </Root>
  )
}

export default withOfferPageAndReduxState((currentState, appState) => {
  const state = takeCurrentOfferState(currentState)

  return {
    offer: state.offer,
    bundleOfferId: state.bundleOffer?.id,
    currentOfferId: state.paginator?.currentOfferId,
    complementaryBundleState: getComplementaryBundleState(state),
    duration: state.duration,
    flights: state.flights,
    enableCalendarRecommendations: !state.bundleOffer ? shouldEnableCalendarRecommendations(state.offer) : false,
    cheapestDays: getCalendarCheapestDays(currentState, appState),
    displayMemberPrice: currentState.displayMemberPrice,
  }
}, OfferBundleAndSaveCalendarSidebarDaysMonth)
